import React, { useState, useCallback, useEffect, memo } from 'react';
import { compose } from 'redux';
import { useQueryClient } from 'react-query';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { get, find, isEmpty } from 'lodash';
import getLocalized from 'utils/get-localized';
import scaleSize from 'utils/scale-size';
import { hideLoader, showLoader } from 'actions/loader';
import useContentNavigation from 'hooks/useContentNavigation';
import useModals from 'hooks/useModals';
import useOnBackClickHandler from 'hooks/useOnBackClickHandler';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import withKeepingLastFocus from 'components/HOCs/withKeepingLastFocus';
import withMouseControls from 'components/HOCs/withMouseControls';
import withScrollHack from 'components/HOCs/withScrollHack';
import CustomCollection from 'components/CustomCollection';
import Platform from 'platform';
import keys from 'config/keys';
import TabContent from './TabContent';
import { StyledSectionPage } from './StyledComponents';
import kibanaVolia from 'helpers/kibanaVolia';
import useLoadCollection from 'hooks/data/useLoadCollection';

const LAT_FOCUS_BASE_KEY = 'SECTION';

const SectionPage = ({ currentMenuItem, isHomePage, lastFocusKeeper }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [detailsUrl, setDetailsUrl] = useState(null);
  const onContentEnter = useContentNavigation();
  const { showExitModal } = useModals();
  const title = getLocalized(currentMenuItem.titles, currentMenuItem.title);
  const tabs = currentMenuItem.subDashboards;
  const lastFocusKey = `${LAT_FOCUS_BASE_KEY}/${currentMenuItem.id}`;
  const { data: collections, isLoading, status } = useLoadCollection(detailsUrl);

  const onConterEnterWrapper = useCallback(
    (item, _index, focusBackConfig) => {
      lastFocusKeeper.save(lastFocusKey, focusBackConfig);
      onContentEnter(item, focusBackConfig);
    },
    [lastFocusKeeper, lastFocusKey, onContentEnter],
  );

  const onTabEnter = useCallback(
    ({ id }) => {
      const currentTab = find(currentMenuItem.subDashboards, { id });
      // Use on tab handler on ig there are no sub dashboards
      // Otherwise only "onSubTabEnter" should emit "detailsUrl" changes
      if (isEmpty(currentTab.subDashboards)) setDetailsUrl(currentTab.detailsUrl);
    },
    [currentMenuItem],
  );

  const onSubTabEnter = useCallback((data) => setDetailsUrl(data.detailsUrl), []);

  useOnBackClickHandler(() => {
    // If we have tabs - back will handle inside TabContent component
    if (isEmpty(tabs)) history.goBack();
  });

  useEffect(() => {
    const isBackFromOtherPage = history.action === 'POP';
    // Resetting the lists if it's not a return
    if (!isBackFromOtherPage) {
      queryClient.resetQueries('COLLECTION_CONTENT_LIST');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKeydown = (e) => {
      if (e.keyCode !== Platform.getKeyCode(keys.BACK)) return;

      const activeElementId = get(document.activeElement, ['id']);

      if (isHomePage) showExitModal({ elementToFocus: `#${activeElementId}` });
    };
    document.addEventListener('keydown', onKeydown, true);
    return () => document.removeEventListener('keydown', onKeydown, true);
  }, [isHomePage, showExitModal]);

  useEffect(() => {
    if (isHomePage) kibanaVolia.postStatInfoEvent();
  }, [isHomePage]);

  useEffect(() => {
    const show = status === 'idle' || isLoading;
    dispatch(show ? showLoader() : hideLoader());
  }, [dispatch, isLoading, status]);

  useEffect(() => {
    if (isEmpty(tabs)) setDetailsUrl(currentMenuItem.detailsUrl);
  }, [currentMenuItem.detailsUrl, tabs]);

  useEffect(() => {
    const focusConfig = lastFocusKeeper.getSaved(lastFocusKey);

    if (focusConfig) {
      setTimeout(() => {
        SpatialNavigationWrapper.focus(focusConfig.sectionToFocus);
        SpatialNavigationWrapper.focus(focusConfig.elementToFocus);
      }, 0);
      lastFocusKeeper.reset(lastFocusKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledSectionPage>
      {!isEmpty(tabs) ? (
        <TabContent
          title={title}
          tabs={tabs}
          detailsUrl={detailsUrl}
          collections={collections}
          isLoading={status === 'idle' || isLoading}
          onTabEnter={onTabEnter}
          onSubTabEnter={onSubTabEnter}
          onContentEnter={onConterEnterWrapper}
          isFoldedByDefault={!!lastFocusKeeper.hasSaved(lastFocusKey)}
        />
      ) : (
        <CustomCollection
          collections={collections}
          isLoading={status === 'idle' || isLoading}
          onContentEnter={onConterEnterWrapper}
          showClock={isHomePage}
          showLogo={isHomePage}
          top={scaleSize(170)}
          left={scaleSize(220)}
          startListScrollIndex={1}
        />
      )}
    </StyledSectionPage>
  );
};

SectionPage.propTypes = {
  currentMenuItem: PropTypes.shape({
    title: PropTypes.string,
    titles: PropTypes.object,
    subDashboards: PropTypes.array.isRequired,
  }).isRequired,
  isHomePage: PropTypes.bool,
};

export default compose(memo, withKeepingLastFocus, withMouseControls, withScrollHack)(SectionPage);
